import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Grid from "@mui/material/Grid";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { getAllRoutes, getAllRoutesByCompany } from "../../services";
import { SaleRoute } from "../../types/sale-route.type";
import { Branch } from "../../types/user.type";
import { STATUS_FILTER } from "./data";

export const ALL = "all";

interface SpecialOrderFilterProps {
  canSelectBranch: boolean;
  branches: Branch[];
  companyId: string;
  setCompanyId: Dispatch<SetStateAction<string>>;
  dateFrom: Dayjs | null;
  setDateFrom: Dispatch<SetStateAction<Dayjs | null>>;
  dateTo: Dayjs | null;
  setDateTo: Dispatch<SetStateAction<Dayjs | null>>;
  status: string;
  setStatus: Dispatch<SetStateAction<string>>;
  routeUuid: string;
  setRouteUuid: Dispatch<SetStateAction<string>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
}

export default function SpecialOrderFilter({
  canSelectBranch,
  branches,
  companyId,
  setCompanyId,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  status,
  setStatus,
  routeUuid,
  setRouteUuid,
  search,
  setSearch,
}: SpecialOrderFilterProps) {
  const [routes, setRoutes] = useState<SaleRoute[]>([]);

  // Special orders live on route 99 as well as the shops' own routes, so this list asks for
  // the SPECIAL route too. A cross-branch user lists the picked branch's routes only.
  useEffect(() => {
    let active = true;
    (async () => {
      if (canSelectBranch && companyId === ALL) {
        setRoutes([]);
        return;
      }
      try {
        const {
          data: { data },
        } = canSelectBranch
          ? await getAllRoutes(companyId, { includeSpecial: true })
          : await getAllRoutesByCompany({ includeSpecial: true });
        if (active) setRoutes(Array.isArray(data) ? data : data?.rows ?? []);
      } catch {
        if (active) setRoutes([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [canSelectBranch, companyId]);

  const routeDisabled = canSelectBranch && companyId === ALL;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
      <Grid container spacing={2}>
        {canSelectBranch && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              size="small"
              fullWidth
              label="สาขา"
              value={companyId}
              onChange={(event) => {
                setCompanyId(event.target.value);
                setRouteUuid(ALL);
              }}
            >
              <MenuItem value={ALL}>ทั้งหมด</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.uuid} value={String(branch.company_id)}>
                  {branch.branch_code} - {branch.branch_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DatePicker
            label="วันที่ส่ง/รับ ตั้งแต่"
            value={dateFrom}
            format="DD/MM/YYYY"
            onChange={(value) => setDateFrom(value)}
            maxDate={dateTo ?? undefined}
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DatePicker
            label="ถึง"
            value={dateTo}
            format="DD/MM/YYYY"
            onChange={(value) => setDateTo(value)}
            minDate={dateFrom ?? undefined}
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="สถานะ"
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            {STATUS_FILTER.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="สายส่ง"
            value={routeUuid}
            disabled={routeDisabled}
            helperText={routeDisabled ? "เลือกสาขาก่อน" : undefined}
            onChange={(event) => setRouteUuid(event.target.value)}
          >
            <MenuItem value={ALL}>ทั้งหมด</MenuItem>
            {routes.map((route) => (
              <MenuItem key={route.uuid} value={route.uuid}>
                {route.route_code} - {route.route_name}
                {route.route_type === "SPECIAL" ? " (สายพิเศษ)" : ""}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: canSelectBranch ? 6 : 9 }}>
          <TextField
            size="small"
            fullWidth
            label="ค้นหา"
            placeholder="เลขที่ออเดอร์ / ชื่อลูกค้า / รหัสลูกค้า"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}
